import React, { lazy } from 'react'

import { Observable } from '@apollo/client'
import {
  FeatureFlagAvailabilitiesDocument,
  PostsGroupHeaderDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type { MetaContext } from 'modules/core/lib/navigation/routes'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { buildCommunityHeaderProps } from 'modules/dashboard/routes/postsDashboard/ResourceHeaders/communityHeader'

const RootPage = React.lazy(() => import('modules/posts/pages/PostsRootPage'))
const CommunitiesFeatureFlagGuard = React.lazy(
  () => import('modules/posts/pages/CommunitiesFeatureFlagGuard')
)
const ShowCommunityPage = lazy(() => import('modules/posts/pages/ShowCommunityPage'))

export const postsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.communities.path,
      element: (
        <CommunitiesFeatureFlagGuard>
          <RootPage />
        </CommunitiesFeatureFlagGuard>
      ),
      handle: {
        module: 'kudos', // TODO: Change to communities when F0 with communities is ready
        moduleName: () => i18n.t('posts.communities.communities_title'),
        meta: () => ({
          title: i18n.t('posts.communities.communities_title'),
        }),
      },
      children: [
        {
          path: navigation.communities.id.path,
          handle: {
            resource: true,
            meta({ watchQuery, params, session }: MetaContext) {
              const featureFlagQuery = watchQuery({
                query: FeatureFlagAvailabilitiesDocument,
                fetchPolicy: 'cache-first',
                variables: {
                  companyId: session.company.id,
                  featureNames: ['dev_communities_page'],
                },
              })

              return featureFlagQuery.flatMap((featureFlagData) => {
                const isFeatureEnabled =
                  featureFlagData.data?.features?.featuresConnection?.nodes?.find(
                    (feature) => feature.name === 'dev_communities_page'
                  )?.enabled ?? false

                if (!isFeatureEnabled) {
                  return new Observable<{ title: string }>((observer) => {
                    observer.next({ title: '' })
                    observer.complete()
                  })
                }

                return watchQuery({
                  query: PostsGroupHeaderDocument,
                  fetchPolicy: 'cache-and-network',
                  variables: {
                    id: Number(params.id),
                  },
                }).map(({ data }) => {
                  const group = data?.posts.group
                  if (!group) {
                    return { title: '' }
                  }
                  return {
                    headerComponentProps: buildCommunityHeaderProps({
                      group,
                    }),
                    title: group?.title ?? '',
                  }
                })
              })
            },
          },
          children: [
            {
              index: true,
              element: <ShowCommunityPage />,
            },
          ],
        },
      ],
    },
  ]
}
